import React, { useRef, useEffect } from 'react';
import { useUniversalProductSearch } from '../../hooks/useUniversalProductSearch';

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

function UniversalProductSearch({ onProductSelected, onClear, selectedProduct }) {
  const { results, isSearching, searchQuery, setSearchQuery, clearResults } = useUniversalProductSearch();
  const [expanded, setExpanded] = React.useState(!selectedProduct);
  const [showResults, setShowResults] = React.useState(false);
  const containerRef = useRef(null);

  // Close results dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (product) => {
    setShowResults(false);
    clearResults();
    setExpanded(false);
    onProductSelected(product);
  };

  const handleClear = () => {
    setExpanded(true);
    onClear();
  };

  return (
    <div className="modern-card universal-search-card" ref={containerRef}>
      <div
        className="universal-search-header"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SearchIcon />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Search Product Catalog</h3>
        </div>
        <ChevronIcon expanded={expanded} />
      </div>

      {selectedProduct && !expanded && (
        <div className="universal-search-selected">
          {selectedProduct.primaryImageUrl && (
            <img
              src={selectedProduct.primaryImageUrl}
              alt={selectedProduct.name}
              className="universal-search-selected-img"
            />
          )}
          <div className="universal-search-selected-info">
            <span className="universal-search-selected-name">{selectedProduct.name}</span>
            {selectedProduct.brand && (
              <span className="universal-search-selected-brand">by {selectedProduct.brand}</span>
            )}
          </div>
          <button
            type="button"
            className="universal-search-clear-btn"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
          >
            <CloseIcon /> Clear
          </button>
        </div>
      )}

      {expanded && (
        <div className="universal-search-body">
          <div className="search-input-wrapper" style={{ marginBottom: results.length > 0 && showResults ? 0 : undefined }}>
            <SearchIcon />
            <input
              type="text"
              className="search-input"
              placeholder="Search by product name, brand, or UPC..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => { if (results.length > 0) setShowResults(true); }}
            />
            {isSearching && <span className="universal-search-spinner" />}
          </div>

          {showResults && results.length > 0 && (
            <div className="universal-search-results">
              {results.map((product) => (
                <div key={product._id} className="universal-search-result-item">
                  <div className="universal-search-result-img-wrapper">
                    {product.primaryImageUrl ? (
                      <img src={product.primaryImageUrl} alt={product.name} />
                    ) : (
                      <div className="universal-search-result-no-img">No img</div>
                    )}
                  </div>
                  <div className="universal-search-result-info">
                    <div className="universal-search-result-name">{product.name}</div>
                    <div className="universal-search-result-meta">
                      {product.brand && <span>{product.brand}</span>}
                      {product.size && <span>{product.size}</span>}
                      {product.upc && <span>UPC: {product.upc}</span>}
                    </div>
                  </div>
                  <div className="universal-search-result-category">
                    {product.category || '—'}
                  </div>
                  <button
                    type="button"
                    className="universal-search-select-btn"
                    onClick={() => handleSelect(product)}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>
          )}

          {showResults && searchQuery.trim().length >= 2 && !isSearching && results.length === 0 && (
            <div className="universal-search-no-results">
              No products found for "{searchQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UniversalProductSearch;
